import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DateField, Form, TextField, InternalPage } from 'theSource/components';
import {
  FORGOT_PASSWORD_INITIAL_VALUES,
  FORGOT_PASSWORD_QUERY_KEY,
  getValidationSchema,
  mapForgotPasswordFormValues,
} from './utils';
import {
  ForgotPasswordFormValues,
  ForgotPasswordRequestDto,
  ForgotPasswordResponseDto,
} from './types';
import { useApiMutation } from 'theSource/utils';
import { GET_CONTENT_SETTINGS_QUERY_KEY } from 'theSource/queries';
import {
  setUserName,
  setToken,
} from 'theSource/features/ForgotPassword/services';
import { ROUTES } from 'theSource/app/routes';

export const ForgotPasswordForm = () => {
  const { t } = useTranslation('pages.forgotPassword');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, mutate: submitData } = useApiMutation<
    ForgotPasswordResponseDto,
    {},
    ForgotPasswordRequestDto
  >({
    queryKey: FORGOT_PASSWORD_QUERY_KEY,
    method: 'put',
    endpoint: '/logon_details',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    onSuccess: (response) => {
      dispatch(setToken(response.token));
      navigate('change');
    },
    getErrorMessage: () => t('messages.forgotPassword:informationDoesntMatch'),
    showSuccessSnackbar: false,
  });

  const handleSubmit = (values: ForgotPasswordFormValues) => {
    submitData(mapForgotPasswordFormValues(values));
    dispatch(setUserName(values.userName));
  };

  return (
    <InternalPage
      title={t('forgotPasswordTitle')}
      withBackLink
      backLinkPath={ROUTES.public.login.path}
    >
      <div className="mt-4">
        <p>
          <span className="red">*</span> = {t('common:required')}
        </p>
      </div>
      <Form<ForgotPasswordFormValues>
        validationSchema={getValidationSchema()}
        onSubmitClick={handleSubmit}
        initialValues={FORGOT_PASSWORD_INITIAL_VALUES}
        isLoading={isLoading}
        enableDirtyFormHandling={false}
        submitButtonLabel={t('continue')}
        submitButtonUseFullWidth
        testId="forgot-password-form"
      >
        <TextField
          required
          id="userName"
          name="userName"
          label={
            <>
              {t('userName')} <span className="sr-only">{t('required')}</span>
            </>
          }
          inputProps={{
            autocapitalize: 'off',
            autocomplete: 'on',
            spellcheck: 'false',
          }}
        />
        <TextField
          required
          id="lastName"
          name="lastName"
          label={
            <>
              {t('lastName')} <span className="sr-only">{t('required')}</span>
            </>
          }
          inputProps={{
            autocapitalize: 'off',
            autocomplete: 'on',
            spellcheck: 'false',
          }}
        />
        <DateField
          required
          id="birthDate"
          name="birthDate"
          label={
            <>
              {t('birthDate')} <span className="sr-only">{t('required')}</span>
            </>
          }
          helperText={t('birthDateHelperText')}
        />
        <TextField
          required
          id="lastFourDigitsOfSsn"
          name="lastFourDigitsOfSsn"
          label={
            <>
              {t('lastFourDigitsOfSsn')}{' '}
              <span className="sr-only">{t('required')}</span>
            </>
          }
          inputProps={{ inputMode: 'numeric' }}
          pattern={/^[0-9]{0,4}$/}
        />
        <Box>
          <h2 className="headline-3">{t('securityTitle')}</h2>
          <Typography variant="body2">{t('securityDescription')}</Typography>
        </Box>
      </Form>
    </InternalPage>
  );
};
